<script setup lang="ts">
import { useI18n } from '@/i18n';
import { logError } from '@/lib/logError';
import { useOnboardingStore } from '@/stores/onboarding';
import type { DisplayOption } from '@/types/onboarding';
import { Button, Heading, Icon, LayoutGap, Prose, Textarea } from '@dabble/quillui';
import { computed, ref } from 'vue';

const { t } = useI18n();
const onboardingStore = useOnboardingStore();

const selectedOption = ref(onboardingStore.state.discoveryMethod);
const discoveryMethodText = ref(onboardingStore.state.otherDiscoveryMethodText || '');
const textInput = ref(discoveryMethodText.value || '');

const options = computed<DisplayOption[]>(() => [
  {
    label: t('onboarding.found.options.welcome_find_internet'),
    value: 'internet',
    icon: 'search',
  },
  {
    label: t('onboarding.found.options.welcome_find_ai'),
    value: 'ai',
    icon: 'brain-circuit',
  },
  {
    label: t('onboarding.found.options.welcome_find_social_media'),
    value: 'social_media',
    icon: 'share',
  },
  {
    label: t('onboarding.found.options.welcome_find_referral'),
    value: 'referral',
    icon: 'megaphone',
  },
  {
    label: t('onboarding.found.options.welcome_find_website'),
    value: 'website',
    icon: 'link',
  },
  {
    label: t('onboarding.found.options.welcome_find_dabble_u'),
    value: 'dabble_u',
    icon: 'graduation-cap',
  },
  {
    label: t('onboarding.found.options.welcome_find_other'),
    value: 'other',
    icon: 'ellipsis',
  },
]);

const emit = defineEmits<{
  (e: 'next'): void;
}>();

const handleNext = async () => {
  if (!selectedOption.value) return;

  try {
    onboardingStore.setDiscoveryMethod(selectedOption.value);
    if (selectedOption.value === 'other') {
      onboardingStore.setOtherDiscoveryMethodText(textInput.value);
    }
    emit('next');
  } catch (error) {
    logError('Failed to save discovery method:', error);
  }
};

const handleSelect = (value: string) => {
  selectedOption.value = value;
  if (value !== 'other') {
    handleNext();
  }
};
</script>

<template>
  <LayoutGap direction="column" align="center" vertical-align="center" size="medium" fill class="found-step">
    <Heading :level="1">{{ t('onboarding.found.title') }}</Heading>
    <Prose variant="secondary" class="found-step__description">
      {{ t('onboarding.found.description') }}
    </Prose>

    <LayoutGap direction="row" wrap align="center" size="small" class="found-step__grid">
      <button
        v-for="option in options"
        :key="option.value"
        type="button"
        class="found-step__tile"
        :class="{ active: selectedOption === option.value }"
        :aria-pressed="selectedOption === option.value"
        :aria-label="option.label || option.value"
        :disabled="onboardingStore.state.isLoading"
        @click="handleSelect(option.value)"
      >
        <LayoutGap
          direction="column"
          align="center"
          vertical-align="center"
          size="xxsmall"
          class="found-step__tile-inner"
        >
          <Icon v-if="option.icon" :name="option.icon" size="large" class="found-step__icon" />
          <div class="found-step__option-label">{{ option.label }}</div>
        </LayoutGap>
      </button>
      <Textarea
        v-if="selectedOption === 'other'"
        id="option-text"
        v-model="textInput"
        class="found-step__other"
        :placeholder="t('onboarding.found.other_placeholder')"
        :disabled="selectedOption !== 'other'"
        :rows="3"
        resize="none"
      />
    </LayoutGap>

    <LayoutGap
      v-if="selectedOption === 'other'"
      direction="row"
      align="right"
      vertical-align="center"
      fill
      size="small"
      class="found-step__actions"
    >
      <Button
        type="button"
        variant="primary"
        finish="filled"
        size="large"
        icon-right="chevron-right"
        :disabled="!selectedOption || onboardingStore.state.isLoading"
        @click="handleNext"
      >
        {{ t('onboarding.found.next') }}
      </Button>
    </LayoutGap>
  </LayoutGap>
</template>

<style lang="scss" scoped>
.found-step {
  width: 100%;
  max-width: 800px;
  margin-inline: auto;
  text-align: center;

  &__grid {
    margin-bottom: space('large');
  }

  &__tile {
    box-sizing: border-box;
    width: 200px;
    min-height: 130px;
    border: 1px solid theme('color-border-primary');
    border-radius: borderRadius('small');
    background: transparent;
    color: theme('color-text-primary');
    padding: space('small');
    text-transform: uppercase;
    font-size: fontSize('small');
    font-weight: fontWeight('semibold');
    letter-spacing: 0.04em;
    transition:
      transform 0.2s ease,
      border-color 0.2s ease,
      background-color 0.2s ease,
      box-shadow 0.2s ease;

    &:not(:disabled):hover:not(.active) {
      transform: translateY(-1px);
      border-color: theme('color-border-interactive');
      background-color: theme('color-bg-app-overlay');
    }

    &.active {
      border-color: theme('color-border-focus');
      background-color: theme('color-bg-app-panel');
      box-shadow: elevation('small');
    }

    &:disabled {
      cursor: not-allowed;
    }
  }

  &__tile-inner {
    width: 100%;
    text-align: center;
    pointer-events: none;
  }

  &__icon {
    font-size: 64px;
  }

  &__option-label {
    width: 100%;
    line-height: lineHeight('compact');
  }

  &__other {
    width: 100%;
    text-align: left;
  }
}
</style>
